import { Link } from 'react-router-dom';
import clsx from 'clsx';

interface KlachtBadgeProps {
  klachtId: string;
  klachtNummer: string;
  status?: string | null;
  size?: 'sm' | 'md';
  /** Optioneel: schakelt link uit (alleen visueel) wanneer false. Default true. */
  linkable?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Rode pill met een klacht-icoon en het klacht-nummer. Linkt naar
 * `/klachten/{id}`. Gebruik op WO-kaarten en in order-detail om meteen
 * zichtbaar te maken dat een werkorder een klacht heeft.
 *
 * Bij gesloten klachten (`Gesloten`/`Opgelost`) wordt de pill grijs zodat
 * actieve klachten visueel domineren.
 */
export function KlachtBadge({
  klachtId,
  klachtNummer,
  status,
  size = 'md',
  linkable = true,
  onClick,
}: KlachtBadgeProps) {
  const isClosed = status === 'Gesloten' || status === 'Opgelost';
  const sizeClasses =
    size === 'sm' ? 'text-[10px] px-1.5 py-0.5 gap-0.5' : 'text-xs px-2 py-0.5 gap-1';
  const colorClasses = isClosed
    ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
    : 'bg-red-600 text-white hover:bg-red-700';

  const content = (
    <>
      <span aria-hidden="true">⚠</span>
      <span>KLACHT {klachtNummer}</span>
    </>
  );

  const className = clsx(
    'inline-flex items-center rounded font-semibold uppercase tracking-wide',
    sizeClasses,
    colorClasses
  );

  const title = `Klacht ${klachtNummer}${status ? ` — ${status.replace(/_/g, ' ')}` : ''}`;

  if (!linkable) {
    return (
      <span className={className} title={title}>
        {content}
      </span>
    );
  }

  return (
    <Link
      to={`/klachten/${klachtId}`}
      onClick={onClick}
      className={className}
      title={title}
    >
      {content}
    </Link>
  );
}
